import DOMPurify from "dompurify";
import { strToU8, type Zippable, zipSync } from "fflate";
import { marked, type Token, type Tokens } from "marked";
import { type FlavourId, flavourOptions } from "./flavours";
import { MARGIN, MEASURE, PAGE_HEIGHT, PAGE_WIDTH } from "./paper";
import { drawnSize, fetchPictures, type Picture, type Pictures, POINTS_PER_PIXEL } from "./pictures";

// Written as WordprocessingML in Word's own styles and lists, so what arrives in Word is a document to keep editing.
export async function docxDocument(text: string, flavour: FlavourId, title: string): Promise<Blob> {
  const tokens = marked.lexer(text, flavourOptions(flavour));
  const writer: Writer = {
    relationships: [],
    lists: [],
    drawings: 0,
    media: await embeddable(await fetchPictures(tokens, RASTERS)),
  };

  const body = blocks(tokens, TOP, writer);
  // Word wants a paragraph after a table that ends the body, and a body with nothing in it still holds one.
  if (body.length === 0 || body.at(-1)?.startsWith("<w:tbl>")) body.push("<w:p/>");

  const files: Zippable = {
    "[Content_Types].xml": strToU8(CONTENT_TYPES),
    "_rels/.rels": strToU8(PACKAGE_RELATIONSHIPS),
    "docProps/core.xml": strToU8(coreProperties(title)),
    "word/document.xml": strToU8(documentPart(body)),
    "word/styles.xml": strToU8(STYLES),
    "word/numbering.xml": strToU8(numberingPart(writer.lists)),
    "word/settings.xml": strToU8(SETTINGS),
    "word/_rels/document.xml.rels": strToU8(documentRelationships(writer.relationships)),
  };
  for (const { target, bytes } of writer.relationships) if (bytes) files[`word/${target}`] = bytes;

  return new Blob([zipSync(files) as Uint8Array<ArrayBuffer>], { type: DOCX_TYPE });
}

export const DOCX_TYPE = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

function blocks(tokens: Token[], frame: Frame, writer: Writer): string[] {
  return tokens.flatMap((token): string[] => {
    switch (token.type) {
      case "heading":
        return [paragraph(runs(token.tokens, writer), frame, { style: `Heading${token.depth}` })];

      case "paragraph":
      case "text":
        return [paragraph(inline(token, writer), frame)];

      case "code":
        return [paragraph(textRun(token.text), frame, { style: "SourceCode" })];

      case "blockquote":
        return blocks(token.tokens ?? [], {
          ...frame,
          indent: frame.indent + QUOTE_INDENT,
          plain: "BlockText",
          quoted: true,
        }, writer);

      case "list":
        return list(token as Tokens.List, frame, writer);

      case "table":
        return [table(token as Tokens.Table, frame, writer)];

      case "hr":
        return [paragraph("", frame, { rule: true })];

      // Raw HTML is markup rather than a document, as it is in the PDF; a task's checkbox is written by its list item.
      case "html":
      case "space":
      case "checkbox":
        return [];

      default:
        return "tokens" in token && Array.isArray(token.tokens) ? blocks(token.tokens, frame, writer) : [];
    }
  });
}

// An item's first paragraph carries its number, or its checkbox, and whatever follows it sits at the item's indent.
function list(token: Tokens.List, frame: Frame, writer: Writer): string[] {
  const level = Math.min(frame.level, LEVELS - 1);
  const numId = token.ordered ? orderedList(writer, level, typeof token.start === "number" ? token.start : 1) : BULLETS;
  const inner: Frame = {
    ...frame,
    indent: frame.indent + LIST_INDENT,
    level: frame.level + 1,
    plain: "ListParagraph",
    loose: token.loose,
  };

  return token.items.flatMap((item) => {
    const tokens = item.tokens.filter((child) => child.type !== "checkbox");
    const opens = tokens[0]?.type === "paragraph" || tokens[0]?.type === "text";
    const words = opens ? inline(tokens[0], writer) : "";

    // A task is ticked or not, which is its marker, so it takes no bullet beside the box as GitHub draws none either.
    const lead = item.task
      ? paragraph(checkbox(item.checked === true) + words, inner)
      : paragraph(words, inner, { list: { numId, level } });

    return [lead, ...blocks(opens ? tokens.slice(1) : tokens, inner, writer)];
  });
}

function table(token: Tokens.Table, frame: Frame, writer: Writer): string {
  const width = twips(MEASURE) - frame.indent;
  const column = Math.floor(width / token.header.length);

  const cell = (cell: Tokens.TableCell, index: number, header: boolean) => {
    const words = runs(cell.tokens, writer, header ? { bold: true } : {});
    const shading = header ? `<w:shd w:val="clear" w:color="auto" w:fill="${HEADER_FILL}"/>` : "";
    const content = paragraph(words, TOP, { justify: JUSTIFY[token.align[index] ?? "left"] });
    return `<w:tc><w:tcPr><w:tcW w:w="${column}" w:type="dxa"/>${shading}</w:tcPr>${content}</w:tc>`;
  };

  // The header row repeats at the top of every page the table runs onto, a table without its header being a grid.
  const header = `<w:tr><w:trPr><w:tblHeader/></w:trPr>${token.header.map((c, i) => cell(c, i, true)).join("")}</w:tr>`;
  const rows = token.rows.map((row) => `<w:tr>${row.map((c, i) => cell(c, i, false)).join("")}</w:tr>`).join("");
  const indent = frame.indent ? `<w:tblInd w:w="${frame.indent}" w:type="dxa"/>` : "";
  const grid = token.header.map(() => `<w:gridCol w:w="${column}"/>`).join("");

  return `<w:tbl><w:tblPr><w:tblStyle w:val="TableGrid"/><w:tblW w:w="${width}" w:type="dxa"/>${indent}`
    + `<w:tblLook w:val="04A0"/></w:tblPr><w:tblGrid>${grid}</w:tblGrid>${header}${rows}</w:tbl>`;
}

function paragraph(content: string, frame: Frame, options: ParagraphOptions = {}): string {
  const style = options.style ?? frame.plain;
  const properties = [
    style === "Normal" ? "" : `<w:pStyle w:val="${style}"/>`,
    options.list
      ? `<w:numPr><w:ilvl w:val="${options.list.level}"/><w:numId w:val="${options.list.numId}"/></w:numPr>`
      : "",
    borders(frame, style, options.rule === true),
    indentation(frame, style, options.list),
    frame.loose && style === "ListParagraph" ? `<w:contextualSpacing w:val="0"/>` : "",
    options.justify ? `<w:jc w:val="${options.justify}"/>` : "",
  ].join("");

  return `<w:p>${properties && `<w:pPr>${properties}</w:pPr>`}${content}</w:p>`;
}

// Only what differs from the style or the list level is written, so Word's own indent buttons still move a paragraph.
function indentation(frame: Frame, style: string, list?: ListMarker): string {
  if (list) {
    return frame.indent === LIST_INDENT * (list.level + 1)
      ? ""
      : `<w:ind w:left="${frame.indent}" w:hanging="${HANGING}"/>`;
  }
  return frame.indent === (STYLE_INDENTS[style] ?? 0) ? "" : `<w:ind w:left="${frame.indent}"/>`;
}

function borders(frame: Frame, style: string, rule: boolean): string {
  const left = frame.quoted && style !== "BlockText" ? `<w:left ${QUOTE_RULE}/>` : "";
  const bottom = rule ? `<w:bottom w:val="single" w:sz="6" w:space="1" w:color="${RULE}"/>` : "";
  return left || bottom ? `<w:pBdr>${left}${bottom}</w:pBdr>` : "";
}

function inline(token: Token, writer: Writer): string {
  const text = token as Tokens.Paragraph | Tokens.Text;
  return text.tokens ? runs(text.tokens, writer) : textRun(text.text);
}

// The marks inside a line, each span of them a run of its own with the marks it carries written on it.
function runs(tokens: Token[] | undefined, writer: Writer, marks: Marks = {}): string {
  return (tokens ?? []).map((token) => {
    switch (token.type) {
      case "strong":
        return runs(token.tokens, writer, { ...marks, bold: true });
      case "em":
        return runs(token.tokens, writer, { ...marks, italic: true });
      case "del":
        return runs(token.tokens, writer, { ...marks, strike: true });
      case "codespan":
        return textRun(token.text, { ...marks, code: true });
      case "link":
        return hyperlink(token as Tokens.Link, writer, marks);
      case "br":
        return LINE_BREAK;
      // A tag arrives apart from the words it wraps, so dropping it keeps them; only a break stands for something.
      case "html":
        return BREAK_TAG.test(token.raw) ? LINE_BREAK : "";
      case "image":
        return picture(token as Tokens.Image, writer, marks);
      // A newline inside a paragraph is a space in every flavour that does not turn it into a break token of its own.
      case "escape":
      case "text":
        return textRun(token.text.replace(/[ \t]*\n[ \t]*/g, " "), marks);
      default:
        return "tokens" in token && Array.isArray(token.tokens)
          ? runs(token.tokens, writer, marks)
          : textRun("raw" in token ? String(token.raw) : "", marks);
    }
  }).join("");
}

// An address the preview's sanitiser would take off a link is the link's words and nothing to click in Word either.
function hyperlink(token: Tokens.Link, writer: Writer, marks: Marks): string {
  if (!token.href || !DOMPurify.isValidAttribute("a", "href", token.href)) return runs(token.tokens, writer, marks);

  const id = relate(writer, { type: HYPERLINK, target: token.href, external: true });
  const words = runs(token.tokens, writer, { ...marks, link: true });
  return `<w:hyperlink r:id="${id}" w:history="1">${words}</w:hyperlink>`;
}

function textRun(text: string, marks: Marks = {}): string {
  const content = xmlSafe(text)
    .split(/(\t|\n)/)
    .map((part) =>
      part === "\t" ? "<w:tab/>" : part === "\n" ? "<w:br/>" : part && `<w:t xml:space="preserve">${escape(part)}</w:t>`
    )
    .join("");

  return content && `<w:r>${runProperties(marks)}${content}</w:r>`;
}

function runProperties(marks: Marks): string {
  const style = marks.code ? "VerbatimChar" : marks.link ? "Hyperlink" : "";
  // A run takes one character style, so code inside a link is dressed as the link by hand.
  const linkedCode = marks.code && marks.link;
  const properties = [
    style && `<w:rStyle w:val="${style}"/>`,
    marks.bold ? "<w:b/><w:bCs/>" : "",
    marks.italic ? "<w:i/><w:iCs/>" : "",
    marks.strike ? "<w:strike/>" : "",
    linkedCode ? `<w:color w:val="${LINK}"/>` : "",
    linkedCode ? `<w:u w:val="single"/>` : "",
  ].join("");

  return properties && `<w:rPr>${properties}</w:rPr>`;
}

// Word's own tickable box, drawn in the face Word draws one in, with the glyph standing in for it wherever it is not.
function checkbox(checked: boolean): string {
  const states = `<w14:checkedState w14:val="2612" w14:font="${BOX_FACE}"/>`
    + `<w14:uncheckedState w14:val="2610" w14:font="${BOX_FACE}"/>`;
  const face = `<w:rFonts w:ascii="${BOX_FACE}" w:eastAsia="${BOX_FACE}" w:hAnsi="${BOX_FACE}" w:hint="eastAsia"/>`;

  return `<w:sdt><w:sdtPr><w14:checkbox><w14:checked w14:val="${checked ? 1 : 0}"/>${states}</w14:checkbox></w:sdtPr>`
    + `<w:sdtContent><w:r><w:rPr>${face}</w:rPr><w:t>${checked ? "&#x2612;" : "&#x2610;"}</w:t></w:r></w:sdtContent>`
    + `</w:sdt><w:r><w:t xml:space="preserve"> </w:t></w:r>`;
}

// A picture that could not be fetched is what the document says it shows, as it is in the PDF.
function picture(token: Tokens.Image, writer: Writer, marks: Marks): string {
  const media = writer.media.get(token.href);
  if (!media) return textRun(token.text || token.title || "", { ...marks, italic: true });

  // Related on first use, so a picture a document shows twice is one file in the package.
  media.ids ??= {
    raster: relate(writer, {
      type: IMAGE,
      target: mediaName(writer, media.raster.extension),
      bytes: media.raster.bytes,
    }),
    svg: media.svg && relate(writer, { type: IMAGE, target: mediaName(writer, "svg"), bytes: media.svg }),
  };

  writer.drawings += 1;
  const id = writer.drawings;
  const name = `name="Picture ${id}" descr="${escape(xmlSafe(token.text))}"`;
  const size = `cx="${media.width}" cy="${media.height}"`;
  // Word 2016 and later draw the SVG and every other reader the PNG beside it, which is how Word saves one itself.
  const vector = media.ids.svg
    ? `<a:extLst><a:ext uri="${SVG_EXTENSION}"><asvg:svgBlip xmlns:asvg="${SVG_NAMESPACE}" `
      + `r:embed="${media.ids.svg}"/></a:ext></a:extLst>`
    : "";

  return `<w:r><w:drawing><wp:inline distT="0" distB="0" distL="0" distR="0"><wp:extent ${size}/>`
    + `<wp:effectExtent l="0" t="0" r="0" b="0"/><wp:docPr id="${id}" ${name}/>`
    + `<wp:cNvGraphicFramePr><a:graphicFrameLocks noChangeAspect="1"/></wp:cNvGraphicFramePr>`
    + `<a:graphic><a:graphicData uri="${PICTURE_NAMESPACE}"><pic:pic><pic:nvPicPr><pic:cNvPr id="${id}" ${name}/>`
    + `<pic:cNvPicPr/></pic:nvPicPr><pic:blipFill><a:blip r:embed="${media.ids.raster}">${vector}</a:blip>`
    + `<a:stretch><a:fillRect/></a:stretch></pic:blipFill><pic:spPr><a:xfrm><a:off x="0" y="0"/>`
    + `<a:ext ${size}/></a:xfrm><a:prstGeom prst="rect"><a:avLst/></a:prstGeom></pic:spPr></pic:pic></a:graphicData>`
    + `</a:graphic></wp:inline></w:drawing></w:r>`;
}

async function embeddable(pictures: Pictures): Promise<Map<string, Media>> {
  const embedded = await Promise.all(
    [...pictures].map(async ([address, picture]) => [address, await embed(picture)] as const),
  );
  return new Map(embedded.flatMap(([address, media]) => media ? [[address, media] as const] : []));
}

async function embed(picture: Picture): Promise<Media | undefined> {
  const { width, height } = drawnSize(picture);
  const size = { width: Math.round(width * EMU_PER_POINT), height: Math.round(height * EMU_PER_POINT) };

  if ("raster" in picture) {
    const bytes = new Uint8Array(await picture.raster.arrayBuffer());
    return { ...size, raster: { extension: EXTENSIONS[picture.raster.type] ?? "png", bytes } };
  }

  const fallback = await rasterise(picture.svg, width, height);
  return fallback && { ...size, raster: { extension: "png", bytes: fallback }, svg: strToU8(picture.svg) };
}

// The PNG an SVG has to travel with, drawn at twice the size it is shown at so it is not soft on a sharp screen.
async function rasterise(svg: string, width: number, height: number): Promise<Uint8Array | undefined> {
  let url = "";
  try {
    url = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml" }));
    const image = new Image();
    image.src = url;
    await image.decode();

    const canvas = document.createElement("canvas");
    canvas.width = Math.ceil(width / POINTS_PER_PIXEL * RASTER_SCALE);
    canvas.height = Math.ceil(height / POINTS_PER_PIXEL * RASTER_SCALE);
    const context = canvas.getContext("2d");
    if (!context) return undefined;
    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    const png = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
    return png ? new Uint8Array(await png.arrayBuffer()) : undefined;
  } catch {
    return undefined;
  } finally {
    if (url) URL.revokeObjectURL(url);
  }
}

function mediaName(writer: Writer, extension: string): string {
  return `media/image${writer.relationships.filter((relationship) => relationship.bytes).length + 1}.${extension}`;
}

// One relationship per address however many links point at it, the package naming a target once.
function relate(writer: Writer, relationship: Omit<Relationship, "id">): string {
  const known = relationship.external
    && writer.relationships.find((r) => r.external && r.target === relationship.target);
  if (known) return known.id;

  const id = `rId${FIXED_RELATIONSHIPS.length + writer.relationships.length + 1}`;
  writer.relationships.push({ ...relationship, id });
  return id;
}

// Each ordered list is an instance of its own that starts where the markdown says, or two lists would count on as one.
function orderedList(writer: Writer, level: number, start: number): number {
  writer.lists.push({ level, start });
  return BULLETS + writer.lists.length;
}

function documentPart(body: string[]): string {
  const page = `<w:pgSz w:w="${twips(PAGE_WIDTH)}" w:h="${twips(PAGE_HEIGHT)}"/>`;
  const margin = twips(MARGIN);
  const margins =
    `<w:pgMar w:top="${margin}" w:right="${margin}" w:bottom="${margin}" w:left="${margin}" w:header="708" `
    + `w:footer="708" w:gutter="0"/>`;

  return `${XML_HEAD}<w:document ${DOCUMENT_NAMESPACES} mc:Ignorable="w14"><w:body>${body.join("")}`
    + `<w:sectPr>${page}${margins}</w:sectPr></w:body></w:document>`;
}

function numberingPart(lists: OrderedList[]): string {
  const level = (index: number, format: string, text: string) => {
    const indent = `<w:ind w:left="${LIST_INDENT * (index + 1)}" w:hanging="${HANGING}"/>`;
    return `<w:lvl w:ilvl="${index}"><w:start w:val="1"/><w:numFmt w:val="${format}"/><w:lvlText w:val="${text}"/>`
      + `<w:lvlJc w:val="left"/><w:pPr>${indent}</w:pPr></w:lvl>`;
  };
  const levels = (format: (index: number) => string) =>
    Array.from({ length: LEVELS }, (_, index) => format(index)).join("");

  const bullets = levels((index) => level(index, "bullet", BULLET_GLYPHS[index % BULLET_GLYPHS.length]));
  const numbers = levels((index) => level(index, "decimal", `%${index + 1}.`));
  const instances = lists.map(({ level, start }, index) =>
    `<w:num w:numId="${BULLETS + index + 1}"><w:abstractNumId w:val="1"/>`
    + `<w:lvlOverride w:ilvl="${level}"><w:startOverride w:val="${start}"/></w:lvlOverride></w:num>`
  ).join("");

  return `${XML_HEAD}<w:numbering xmlns:w="${W}">`
    + `<w:abstractNum w:abstractNumId="0"><w:multiLevelType w:val="hybridMultilevel"/>${bullets}</w:abstractNum>`
    + `<w:abstractNum w:abstractNumId="1"><w:multiLevelType w:val="hybridMultilevel"/>${numbers}</w:abstractNum>`
    + `<w:num w:numId="${BULLETS}"><w:abstractNumId w:val="0"/></w:num>${instances}</w:numbering>`;
}

function documentRelationships(relationships: Relationship[]): string {
  const entries = [...FIXED_RELATIONSHIPS, ...relationships].map(({ id, type, target, external }) =>
    `<Relationship Id="${id}" Type="${RELATIONSHIP_TYPES}/${type}" Target="${escape(xmlSafe(target))}"`
    + `${external ? ` TargetMode="External"` : ""}/>`
  );
  return `${XML_HEAD}<Relationships xmlns="${PACKAGE_RELATIONSHIPS_NAMESPACE}">${entries.join("")}</Relationships>`;
}

function coreProperties(title: string): string {
  return `${XML_HEAD}<cp:coreProperties xmlns:cp="${CORE_PROPERTIES}" xmlns:dc="http://purl.org/dc/elements/1.1/">`
    + `<dc:title>${escape(xmlSafe(title))}</dc:title></cp:coreProperties>`;
}

function twips(points: number): number {
  return Math.round(points * 20);
}

function escape(text: string): string {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

// XML 1.0 has no way to spell a NUL, most control characters or a lone surrogate, and Word refuses a part holding one.
function xmlSafe(text: string): string {
  let safe = "";
  for (const character of text) {
    const code = character.codePointAt(0) ?? 0;
    const allowed = code === 0x9 || code === 0xa || code === 0xd || (code >= 0x20 && code < 0xd800)
      || (code >= 0xe000 && code < 0xfffe) || code > 0xffff;
    if (allowed) safe += character;
  }
  return safe;
}

interface Writer {
  relationships: Relationship[];
  lists: OrderedList[];
  drawings: number;
  media: Map<string, Media>;
}

interface Relationship {
  id: string;
  type: string;
  target: string;
  external?: boolean;
  bytes?: Uint8Array;
}

interface OrderedList {
  level: number;
  start: number;
}

// A picture as it goes into the package, sized in EMU, with the relationships it is drawn by once it has been drawn.
interface Media {
  width: number;
  height: number;
  raster: { extension: string; bytes: Uint8Array };
  svg?: Uint8Array;
  ids?: { raster: string; svg?: string };
}

// Where a block is being written: how far in it starts, the list level a list opened there takes, and what prose is.
interface Frame {
  indent: number;
  level: number;
  plain: "Normal" | "ListParagraph" | "BlockText";
  quoted: boolean;
  loose: boolean;
}

interface ParagraphOptions {
  style?: string;
  list?: ListMarker;
  rule?: boolean;
  justify?: string;
}

interface ListMarker {
  numId: number;
  level: number;
}

interface Marks {
  bold?: boolean;
  italic?: boolean;
  strike?: boolean;
  code?: boolean;
  link?: boolean;
}

const TOP: Frame = { indent: 0, level: 0, plain: "Normal", quoted: false, loose: false };

const BREAK_TAG = /^<br\s*\/?>$/i;

const BOX_FACE = "MS Gothic";
const LINE_BREAK = "<w:r><w:br/></w:r>";

const RASTERS = ["image/png", "image/jpeg", "image/gif"];
const EXTENSIONS: Record<string, string> = { "image/png": "png", "image/jpeg": "jpeg", "image/gif": "gif" };
const RASTER_SCALE = 2;
const EMU_PER_POINT = 12700;

// Twips, which are twentieths of a point: a list level steps in half an inch and hangs its marker a quarter of one.
const LIST_INDENT = 720;
const HANGING = 360;
const QUOTE_INDENT = 360;
const LEVELS = 9;

// The indent each style gives a paragraph of its own accord, which a frame only writes over where it has to.
const STYLE_INDENTS: Record<string, number> = { ListParagraph: LIST_INDENT, BlockText: QUOTE_INDENT };

// The disc, circle and square a browser draws at the first three levels, spelled as references so no face is named.
const BULLET_GLYPHS = ["&#x2022;", "&#x25E6;", "&#x25AA;"];

// The bullet list is one instance every unordered list shares, and each ordered list is numbered after it.
const BULLETS = 1;

const JUSTIFY: Record<string, string | undefined> = { center: "center", right: "right" };

const RULE = "DEE2E6";
const LINK = "1971C2";
const HEADER_FILL = "F8F9FA";
const QUOTE_RULE = `w:val="single" w:sz="18" w:space="8" w:color="${RULE}"`;

const HYPERLINK = "hyperlink";
const IMAGE = "image";

const XML_HEAD = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n`;
const W = "http://schemas.openxmlformats.org/wordprocessingml/2006/main";
const RELATIONSHIP_TYPES = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";
const PACKAGE_RELATIONSHIPS_NAMESPACE = "http://schemas.openxmlformats.org/package/2006/relationships";
const CORE_PROPERTIES = "http://schemas.openxmlformats.org/package/2006/metadata/core-properties";
const PICTURE_NAMESPACE = "http://schemas.openxmlformats.org/drawingml/2006/picture";
const SVG_NAMESPACE = "http://schemas.microsoft.com/office/drawing/2016/SVG/main";
const SVG_EXTENSION = "{96DAC541-7B7A-43D3-8B79-37D633B846F1}";

const DOCUMENT_NAMESPACES = [
  `xmlns:w="${W}"`,
  `xmlns:r="${RELATIONSHIP_TYPES}"`,
  `xmlns:wp="http://schemas.openxmlformats.org/drawingml/2006/wordprocessingDrawing"`,
  `xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main"`,
  `xmlns:pic="${PICTURE_NAMESPACE}"`,
  `xmlns:w14="http://schemas.microsoft.com/office/word/2010/wordml"`,
  `xmlns:mc="http://schemas.openxmlformats.org/markup-compatibility/2006"`,
].join(" ");

const FIXED_RELATIONSHIPS: Relationship[] = [
  { id: "rId1", type: "styles", target: "styles.xml" },
  { id: "rId2", type: "numbering", target: "numbering.xml" },
  { id: "rId3", type: "settings", target: "settings.xml" },
];

const WORDPROCESSING = "application/vnd.openxmlformats-officedocument.wordprocessingml";

const CONTENT_TYPES = `${XML_HEAD}<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">`
  + `<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>`
  + `<Default Extension="xml" ContentType="application/xml"/>`
  + `<Default Extension="png" ContentType="image/png"/><Default Extension="jpeg" ContentType="image/jpeg"/>`
  + `<Default Extension="gif" ContentType="image/gif"/><Default Extension="svg" ContentType="image/svg+xml"/>`
  + `<Override PartName="/word/document.xml" ContentType="${WORDPROCESSING}.document.main+xml"/>`
  + `<Override PartName="/word/styles.xml" ContentType="${WORDPROCESSING}.styles+xml"/>`
  + `<Override PartName="/word/numbering.xml" ContentType="${WORDPROCESSING}.numbering+xml"/>`
  + `<Override PartName="/word/settings.xml" ContentType="${WORDPROCESSING}.settings+xml"/>`
  + `<Override PartName="/docProps/core.xml" ContentType="application/vnd.openxmlformats-package.core-properties+xml"/>`
  + `</Types>`;

const PACKAGE_RELATIONSHIPS = `${XML_HEAD}<Relationships xmlns="${PACKAGE_RELATIONSHIPS_NAMESPACE}">`
  + `<Relationship Id="rId1" Type="${RELATIONSHIP_TYPES}/officeDocument" Target="word/document.xml"/>`
  + `<Relationship Id="rId2" Type="${PACKAGE_RELATIONSHIPS_NAMESPACE}/metadata/core-properties" `
  + `Target="docProps/core.xml"/></Relationships>`;

// Without a compatibility mode Word opens a document as one written by an older Word and says so in its title bar.
const SETTINGS = `${XML_HEAD}<w:settings xmlns:w="${W}"><w:defaultTabStop w:val="720"/>`
  + `<w:characterSpacingControl w:val="doNotCompress"/><w:compat><w:compatSetting w:name="compatibilityMode" `
  + `w:uri="http://schemas.microsoft.com/office/word" w:val="15"/></w:compat></w:settings>`;

const HEADING_SIZES = [42, 33, 28, 24, 22, 21];

const style = (type: string, id: string, name: string, body: string, flags = "") =>
  `<w:style w:type="${type}"${flags} w:styleId="${id}"><w:name w:val="${name}"/>${body}</w:style>`;
const size = (halfPoints: number) => `<w:sz w:val="${halfPoints}"/><w:szCs w:val="${halfPoints}"/>`;
const border = (space: number) => (side: string) =>
  `<w:${side} w:val="single" w:sz="4" w:space="${space}" w:color="${RULE}"/>`;
const cellMargins = (vertical: number, horizontal: number) =>
  `<w:tblCellMar><w:top w:w="${vertical}" w:type="dxa"/><w:left w:w="${horizontal}" w:type="dxa"/>`
  + `<w:bottom w:w="${vertical}" w:type="dxa"/><w:right w:w="${horizontal}" w:type="dxa"/></w:tblCellMar>`;

const DEFAULT = ` w:default="1"`;
const CUSTOM = ` w:customStyle="1"`;
const HIDDEN = `<w:uiPriority w:val="99"/><w:semiHidden/><w:unhideWhenUsed/>`;
const COURIER = `<w:rFonts w:ascii="Courier New" w:hAnsi="Courier New" w:cs="Courier New"/><w:noProof/>`;
const CODE_FILL = `<w:shd w:val="clear" w:color="auto" w:fill="F1F3F5"/>`;
const SINGLE_SPACED = `<w:spacing w:after="0" w:line="240" w:lineRule="auto"/>`;
const BOX = ["top", "left", "bottom", "right"].map(border(4)).join("");
const GRID = ["top", "left", "bottom", "right", "insideH", "insideV"].map(border(0)).join("");

// Sizes and faces are the PDF's and names are Word's own; spacing sits in the defaults so a table's style can close it.
const STYLES = `${XML_HEAD}<w:styles xmlns:w="${W}">` + [
  `<w:docDefaults><w:rPrDefault><w:rPr><w:rFonts w:ascii="Roboto" w:hAnsi="Roboto" w:cs="Roboto"/>${size(21)}</w:rPr>`
  + `</w:rPrDefault><w:pPrDefault><w:pPr><w:spacing w:after="160" w:line="324" w:lineRule="auto"/></w:pPr>`
  + `</w:pPrDefault></w:docDefaults>`,
  style("paragraph", "Normal", "Normal", "<w:qFormat/>", DEFAULT),
  style("character", "DefaultParagraphFont", "Default Paragraph Font", HIDDEN, DEFAULT),
  style("table", "TableNormal", "Normal Table", `${HIDDEN}<w:tblPr>${cellMargins(0, 108)}</w:tblPr>`, DEFAULT),
  style("numbering", "NoList", "No List", HIDDEN, DEFAULT),
  ...HEADING_SIZES.map((halfPoints, index) => {
    const spacing = `<w:spacing w:before="${index === 0 ? 0 : 240}" w:after="120"/>`;
    const layout = `<w:pPr><w:keepNext/><w:keepLines/>${spacing}<w:outlineLvl w:val="${index}"/></w:pPr>`;
    const face = `<w:rPr><w:b/><w:bCs/>${size(halfPoints)}</w:rPr>`;
    const head = `<w:basedOn w:val="Normal"/><w:next w:val="Normal"/><w:uiPriority w:val="9"/><w:qFormat/>`;
    return style("paragraph", `Heading${index + 1}`, `heading ${index + 1}`, head + layout + face);
  }),
  style(
    "paragraph",
    "ListParagraph",
    "List Paragraph",
    `<w:basedOn w:val="Normal"/><w:uiPriority w:val="34"/><w:qFormat/>`
      + `<w:pPr><w:ind w:left="${LIST_INDENT}"/><w:contextualSpacing/></w:pPr>`,
  ),
  style(
    "paragraph",
    "BlockText",
    "Block Text",
    `<w:basedOn w:val="Normal"/><w:uiPriority w:val="99"/><w:unhideWhenUsed/>`
      + `<w:pPr><w:pBdr><w:left ${QUOTE_RULE}/></w:pBdr><w:ind w:left="${QUOTE_INDENT}"/></w:pPr>`,
  ),
  style(
    "paragraph",
    "SourceCode",
    "Source Code",
    `<w:basedOn w:val="Normal"/><w:qFormat/><w:pPr><w:pBdr>${BOX}</w:pBdr>${CODE_FILL}`
      + `<w:spacing w:after="200" w:line="240" w:lineRule="auto"/></w:pPr>`
      + `<w:rPr>${COURIER}${size(18)}</w:rPr>`,
    CUSTOM,
  ),
  style(
    "character",
    "VerbatimChar",
    "Verbatim Char",
    `<w:basedOn w:val="DefaultParagraphFont"/><w:rPr>${COURIER}${size(19)}${CODE_FILL}</w:rPr>`,
    CUSTOM,
  ),
  style(
    "character",
    "Hyperlink",
    "Hyperlink",
    `<w:basedOn w:val="DefaultParagraphFont"/><w:uiPriority w:val="99"/><w:unhideWhenUsed/>`
      + `<w:rPr><w:color w:val="${LINK}"/><w:u w:val="single"/></w:rPr>`,
  ),
  style(
    "table",
    "TableGrid",
    "Table Grid",
    `<w:basedOn w:val="TableNormal"/><w:uiPriority w:val="39"/><w:pPr>${SINGLE_SPACED}</w:pPr><w:tblPr><w:tblBorders>`
      + `${GRID}</w:tblBorders>`
      + `${cellMargins(60, 120)}</w:tblPr>`,
  ),
].join("") + `</w:styles>`;
